import { z } from "zod";

const scoredProfileArtistWireSchema = z.object({
  artistId: z.string(),
  artistName: z.string(),
  artistImage: z.string().optional(),
  streamCount: z.number(),
  streamTime: z.number(),
  accessesCount: z.number(),
  moneySpentStreaming: z.number(),
  moneySpentD2C: z.number(),
  score: z.number(),
  level: z.number(),
  levelName: z.string(),
  nextLevelName: z.string().nullable(),
  levelProgress: z.number(),
  shadowColor: z.string(),
});

const publicScoredProfileArtistWireSchema = scoredProfileArtistWireSchema.omit({
  moneySpentStreaming: true,
  moneySpentD2C: true,
});

const profileStatsPayloadWireSchema = z.object({
  totalStreams: z.number(),
  totalStreamTime: z.number(),
  moneySpentStreaming: z.number(),
  moneySpentD2C: z.number(),
  accesses: z.number(),
  gemsEarned: z.number(),
  topArtists: z.array(scoredProfileArtistWireSchema),
  topGenres: z.array(
    z.object({
      genreId: z.string(),
      genreName: z.string(),
      streamCount: z.number(),
    }),
  ),
  lastUpdatedAt: z.string(),
  lastStreamAt: z.string().optional(),
});

const publicProfileStatsPayloadWireSchema = z.object({
  totalStreams: z.number(),
  totalStreamTime: z.number(),
  accesses: z.number(),
  topArtists: z.array(publicScoredProfileArtistWireSchema),
  lastUpdatedAt: z.string(),
});

const profileStatsThisMonthWireSchema = profileStatsPayloadWireSchema.omit({
  lastStreamAt: true,
});

export const profileUserStatsWireSchema = profileStatsPayloadWireSchema.extend({
  score: z.number(),
  level: z.number(),
  levelName: z.string(),
  nextLevelName: z.string().nullable(),
  levelProgress: z.number(),
  thisMonth: profileStatsThisMonthWireSchema.optional(),
  scoringLevels: z.array(
    z.object({
      minScore: z.number(),
      level: z.number(),
      name: z.string(),
      vibe: z.string(),
      shadowColor: z.string(),
    }),
  ),
});

export type ProfileUserStatsWire = z.infer<typeof profileUserStatsWireSchema>;

export const publicProfileUserStatsWireSchema =
  publicProfileStatsPayloadWireSchema.extend({
    score: z.number(),
    level: z.number(),
    levelName: z.string(),
    nextLevelName: z.string().nullable(),
    levelProgress: z.number(),
    thisMonth: publicProfileStatsPayloadWireSchema.optional(),
    scoringLevels: z.array(
      z.object({
        minScore: z.number(),
        level: z.number(),
        name: z.string(),
        vibe: z.string(),
        shadowColor: z.string(),
      }),
    ),
  });

export type PublicProfileUserStatsWire = z.infer<
  typeof publicProfileUserStatsWireSchema
>;

/**
 * Wire-shape for `user.stats.getArtistSongBreakdown`. Used by the durable
 * cache so the artist song breakdown panel renders from cache when the
 * device is offline. The breakdown is server-derived analytics (not
 * something the device can compute locally), so a simple cache-then-
 * refetch is sufficient.
 */
export const artistSongBreakdownWireSchema = z.object({
  songs: z.array(
    z.object({
      songId: z.string(),
      songTitle: z.string(),
      streamCount: z.number(),
      totalStreamTime: z.number(),
      moneySpentStreaming: z.number(),
    }),
  ),
});

export type ArtistSongBreakdown = z.infer<
  typeof artistSongBreakdownWireSchema
>;
